<template>
  <ion-page>
    <ion-content v-if="isDirectusReady && profileRef?.value" :fullscreen="true">
      <round-header
        :title="`Hej ${user?.displayName?.split(' ')[0]}!`"
        :database="characters"
        :image="profileRef.value.avatar"
        image-type="character"
        :edit-mode="editMode"
        @pick-icon="setCharacter"
        @toggle-edit-mode="editMode = !editMode"
      >
        <!-- Friends avatars -->
        <div class="absolute -bottom-25 left-[58%] mt-4 flex justify-center">
          <div v-for="friend in friendsData" :key="friend.id" class="relative -mx-8">
            <!-- Friend name on top of head -->
            <div
              class="font-fredoka font-outline-1 absolute -top-6 left-1/2 -translate-x-1/2 transform text-center text-sm whitespace-nowrap text-white"
            >
              {{ friend.name }}
            </div>
            <!-- Friend avatar -->
            <img :src="friend.avatarUrl" class="h-48 w-48 object-contain" :alt="friend.name" />
          </div>
        </div>
      </round-header>

      <!-- Date picker -->
      <div v-if="false" class="my-4 flex items-center justify-center">
        <button class="rounded-l-lg bg-gray-200 px-4 py-2 hover:bg-gray-300" @click="dayOffset--">
          &lt; Previous
        </button>
        <div class="bg-gray-100 px-4 py-2">
          {{ currentDateFormatted }}
        </div>
        <button
          class="rounded-r-lg bg-gray-200 px-4 py-2 hover:bg-gray-300"
          :disabled="dayOffset >= 0"
          @click="dayOffset++"
        >
          Next &gt;
        </button>
      </div>

      <div id="container">
        <div class="flex flex-wrap justify-center">
          <template v-for="category in categories" :key="category.id">
            <category-button
              :class="editMode ? 'shake' : ''"
              :icon="category.icon"
              :value="category.getScore(dayOffset)"
              :max="category.dailyGoal"
              class="m-4"
              @click="router.push('/category/' + category.id)"
            />
            <div v-if="false">
              <template v-for="score in category.getScores(dayOffset)" :key="score.id">
                <template
                  v-for="task in (tasks as Task[]).filter((e) => e.id == score.task)"
                  :key="task.id"
                >
                  {{ task.name }}
                  <directus-image v-if="task" :src="task.icon" class="w-12 cursor-pointer" />
                </template>
                <!-- TODO: Show shared tasks in activity !-->
              </template>
            </div>
          </template>
          <category-button v-if="editMode" :icon="ICON_PLUS" class="m-4" @click="addCategory" />
        </div>
        <div v-if="editMode" class="absolute right-0 bottom-0 flex bg-black px-4 py-2 text-white">
          <button
            class="mx-2 cursor-pointer bg-gray-800 p-2 hover:bg-gray-600"
            @click="refreshImages"
          >
            Refresh Images
          </button>
          <button class="mx-2 cursor-pointer bg-gray-800 p-2 hover:bg-gray-600" @click="nextDay">
            Next Day
          </button>
          <button
            v-if="false"
            class="mx-2 cursor-pointer bg-gray-800 p-2 hover:bg-gray-600"
            @click="deleteAll"
          >
            Delete All
          </button>
        </div>
      </div>
    </ion-content>
    <ion-content v-else>
      <template v-if="!isDirectusReady"> Loading Data from Directus... </template>
      <template v-else-if="profileRef?.value == null"> Fetching profile... </template>
      <template v-else> Unknown error. </template>
    </ion-content>
  </ion-page>
</template>

<script lang="ts" setup>
import { IonContent, IonPage, onIonViewWillEnter } from '@ionic/vue';
import { characters, ICON_PLUS, isDirectusReady } from '../services/Directus.ts';
import CategoryButton from '../components/CategoryButton.vue';
import { Category } from '../models/Category.ts';
import { ref, type Ref, computed, watch } from 'vue';
import { saveAndSync, usePreferencesCollection } from '../composable/usePreferenceCollection.ts';
import { PreferencesDB } from '../services/PreferencesDB.ts';
import router from '../router';
import RoundHeader from '../components/RoundHeader.vue';
import { CacheService } from '../services/CacheService.ts';
import { useCurrentUser } from 'vuefire';
import { profileRef, userId } from '../services/FirebaseDB.ts';
import { userService, type UserData } from '../services/UserService.ts';
import { collection, doc, getFirestore, updateDoc } from 'firebase/firestore';
import { Task } from '../models/Task.ts';
import DirectusImage from '../components/DirectusImage.vue';
import { useCollection } from 'vuefire';

const categories: Ref<Category[]> = usePreferencesCollection(Category);
const user = useCurrentUser();
const editMode: Ref<boolean> = ref<boolean>(false);
const dayOffset: Ref<number> = ref<number>(0);
const friendsData: Ref<UserData[]> = ref<UserData[]>([]);
const isLoadingFriends: Ref<boolean> = ref<boolean>(false);

const db = getFirestore();
const tasks = useCollection(() =>
  userId.value ? collection(db, 'users', userId.value, 'tasks') : null
);

const currentDateFormatted = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset.value);

  // Show relative dates for today, yesterday, and tomorrow
  if (dayOffset.value === 0) {
    return 'Today';
  } else if (dayOffset.value === -1) {
    return 'Yesterday';
  } else if (dayOffset.value === 1) {
    return 'Tomorrow';
  } else {
    // For other days, show formatted date like "Mi., 17.02."
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  }
});

async function addCategory() {
  const category = new Category();
  await saveAndSync(Category, category);
}

function deleteAll() {
  PreferencesDB.clearNamespace(Category);
}

function refreshImages() {
  const cache = new CacheService();
  cache.remove('files');
}

async function nextDay() {
  for (const category of categories.value) {
    category.scores.forEach((score) => (score.createdAt -= 1000 * 60 * 60 * 24));
    saveAndSync(Category, category);
  }
}

async function setCharacter(id: string) {
  if (profileRef.value == undefined || user.value == null) {
    return;
  }

  // Update Firebase directly - VueFire will automatically update the profile computed property
  const db = getFirestore();
  const profileDocRef = doc(db, `users/${user.value.uid}/profile/default`);
  await updateDoc(profileDocRef, {
    avatar: id,
    lastModified: Date.now(),
  });
}

async function loadFriendsData() {
  if (
    !profileRef.value ||
    !profileRef.value.sharedWith ||
    profileRef.value.sharedWith.length === 0
  ) {
    friendsData.value = [];
    return;
  }

  isLoadingFriends.value = true;
  try {
    friendsData.value = await userService.getUsersData(profileRef.value.sharedWith);
  } catch (error) {
    console.error('Error loading friends data:', error);
  } finally {
    isLoadingFriends.value = false;
  }
}

// Watch for changes to the profile to reload friends data
watch(
  () => profileRef.value?.sharedWith,
  async () => {
    await loadFriendsData();
  },
  { deep: true }
);

onIonViewWillEnter(() => {
  editMode.value = false;
  dayOffset.value = 0; // Reset to current day when entering the view
  loadFriendsData();
});
</script>
